import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import api from '../lib/api';
import { LogIn, UserPlus, Mail, Lock, User, Phone, MapPin, Globe, Tag, Loader2, Eye, EyeOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import WelcomeBackModal from './WelcomeBackModal';
import RoleSelectionModal from './RoleSelectionModal';

// Identity roles for multi-role detection
// Note: Backend normalizes "creator" → "author", so we check both
const IDENTITY_ROLES = ['reader', 'creator', 'author', 'publisher', 'admin'];

// Normalize role - treat "author" as "creator" for frontend consistency
const normalizeRole = (role) => {
  if (role === 'author') return 'creator';
  return role;
};

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken, getHomePage, needsRoleSelection, handleRoleSelection, getIdentityRoles, setActiveRole } = useAuth();
  const { notifyError, notifyBilling, notifyInfo } = useNotification();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showInvalidLoginModal, setShowInvalidLoginModal] = useState(false);
  const [showWelcomeBackModal, setShowWelcomeBackModal] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  
  // Role selection modal state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleModalUser, setRoleModalUser] = useState(null);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Registration duplicate-detection warning & verification gate state
  const [emailWarning, setEmailWarning] = useState(null);
  const [verificationPending, setVerificationPending] = useState(null); // { email } when set
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  
  // Password visibility toggle
  const [showPassword, setShowPassword] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    alternate_email: '',
    city: '',
    state: '',
    country: '',
    promo_code: ''  // Optional promo code field
  });
  
  const [errors, setErrors] = useState({});

  // Pre-populate promo code from URL query parameter (?promo=GETNEWAUDIA)
  // Used by the Kiosk Module post-event email link and any direct
  // /register?promo=CODE link. Switches the form to registration mode
  // automatically so the user lands on the right tab.
  useEffect(() => {
    const urlPromo = searchParams.get('promo');
    if (urlPromo && urlPromo.trim()) {
      const normalized = urlPromo.trim().toUpperCase();
      setFormData(prev => ({ ...prev, promo_code: normalized }));
      setIsLogin(false);
    }
  }, [searchParams]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Normalize email fields: trim + lowercase before storing (Section 9.1)
    const normalized = (name === 'email' || name === 'alternate_email')
      ? value.trim().toLowerCase()
      : value;
    setFormData(prev => ({
      ...prev,
      [name]: normalized
    }));
    // Clearing email warning when user edits the email
    if (name === 'email' && emailWarning) {
      setEmailWarning(null);
    }
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleResendVerification = async (emailToResend) => {
    setResendLoading(true);
    setResendMessage(null);
    try {
      await api.post('/auth/resend-verification', { email: emailToResend });
      setResendMessage('If an unverified account exists, a new verification link has been sent.');
    } catch (err) {
      setResendMessage('If an unverified account exists, a new verification link has been sent.');
    } finally {
      setResendLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (!isLogin) {
      // Validate onboarding fields
      if (!formData.first_name?.trim()) newErrors.first_name = 'First name is required';
      if (!formData.last_name?.trim()) newErrors.last_name = 'Last name is required';
      if (!formData.phone?.trim()) newErrors.phone = 'Phone number is required';
      if (!formData.alternate_email) {
        newErrors.alternate_email = 'Alternate email is required';
      } else if (!validateEmail(formData.alternate_email)) {
        newErrors.alternate_email = 'Invalid email format';
      }
      if (!formData.city?.trim()) newErrors.city = 'City is required';
      if (!formData.state?.trim()) newErrors.state = 'State is required';
      if (!formData.country?.trim()) newErrors.country = 'Country is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle role selection from modal
  const onRoleSelected = (selectedRole) => {
    setShowRoleModal(false);
    setActiveRole(selectedRole);
    const targetRoute = handleRoleSelection(selectedRole);
    navigate(targetRoute);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      if (isLogin) {
        // Login flow
        const response = await api.post('/auth/login', {
          email: formData.email,
          password: formData.password
        });
        const { access_token } = response.data;
        const userData = await loginWithToken(access_token);
        
        // Extract identity roles for multi-role detection
        // Normalize "author" → "creator" for frontend consistency
        const userRoles = userData?.roles || [];
        const normalizedRoles = userRoles.map(normalizeRole);
        const identityRoles = [...new Set(normalizedRoles.filter(role => 
          ['reader', 'creator', 'publisher', 'admin'].includes(role)
        ))];
        
        // ADMIN OVERRIDE: Admin users always go directly to Admin dashboard
        // No role selection modal, no other options
        if (normalizedRoles.includes('admin')) {
          setActiveRole('admin');
          navigate('/admin');
          return;
        }
        
        // Check if user has multiple identity roles
        if (identityRoles.length >= 2) {
          // Check for saved preferred role (from "Remember my choice")
          const preferredRole = localStorage.getItem('preferredRole');
          const normalizedPreferred = preferredRole ? normalizeRole(preferredRole) : null;
          
          // If user has a saved preference AND it's a valid role for them, auto-select it
          if (normalizedPreferred && identityRoles.includes(normalizedPreferred)) {
            setActiveRole(normalizedPreferred);
            const targetRoute = getHomePage(userData, normalizedPreferred);
            navigate(targetRoute);
            return;
          }
          
          // No saved preference or invalid - show role selection modal
          setRoleModalUser(userData);
          setShowRoleModal(true);
          return; // Don't navigate yet - wait for role selection
        }
        
        // Single identity role - auto-set active role and route
        const singleRole = identityRoles.length === 1 ? identityRoles[0] : 'reader';
        setActiveRole(singleRole);
        
        // Check if user is a reader only
        const isReaderOnly = singleRole === 'reader';
        
        if (isReaderOnly) {
          // Show Welcome Back modal for readers
          setLoggedInUser(userData);
          setShowWelcomeBackModal(true);
          // UI transition to modal is sufficient feedback
        } else {
          // Non-reader users go directly to their home page
          const homePage = getHomePage(userData, singleRole);
          // Navigation is sufficient feedback
          navigate(homePage);
        }
      } else {
        // Registration flow with all onboarding fields
        const response = await api.post('/auth/register', {
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          alternate_email: formData.alternate_email,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          promo_code: formData.promo_code.trim() || null  // Send promo code if provided
        });
        const { access_token, user: registeredUser } = response.data;

        // Surface duplicate-detection warning if backend flagged similar email/name
        if (registeredUser?.registration_warning) {
          setEmailWarning(registeredUser.registration_warning);
        }

        // Verification gate (Section 9.3): if backend returned verification_pending,
        // show a confirmation screen instead of logging into dashboard.
        if (registeredUser?.verification_pending) {
          setVerificationPending({ email: registeredUser.email });
          return; // Do not auto-login or navigate
        }

        // Legacy / verified path
        const userData = await loginWithToken(access_token);

        // New registrations are always readers - set active role
        setActiveRole('reader');
        
        // Check if promo code was applied and show appropriate message
        if (registeredUser?.promo_code_applied) {
          notifyBilling(`Promo code applied — ${registeredUser.credits_balance} startup credits awarded!`);
        }
        // Account creation reflected in navigation - no additional notification needed
        
        // Role-based redirect
        const homePage = getHomePage(userData);
        navigate(homePage);
      }
    } catch (error) {
      console.error('Auth error:', error);
      
      // Check for account suspended error (banned user)
      const errorDetail = error.response?.data?.detail;
      if (error.response?.status === 403 && errorDetail?.error === 'account_suspended') {
        notifyError('Your account has been suspended. If you believe this is an error, please contact support@newaudia.com');
        return;
      }
      
      if (isLogin && error.response?.status === 401) {
        // Show invalid login modal
        setShowInvalidLoginModal(true);
      } else {
        const errorMessage = typeof errorDetail === 'string' ? errorDetail : 
                            errorDetail?.message ||
                            (isLogin ? 'Login failed' : 'Registration failed');
        notifyError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    notifyInfo('Google OAuth coming soon! Please use email registration for now.');
  };

  const handleCreateNewAccount = () => {
    setShowInvalidLoginModal(false);
    setIsLogin(false);
  };

  const handleTryAgain = () => {
    setShowInvalidLoginModal(false);
    // Keep email filled
  };

  return (
    <>
      {/* Background - only on desktop for context */}
      <div className="hidden sm:block fixed inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-cyan-900">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
      </div>

      {/* Modal Container */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="w-full max-w-md my-8">
          {/* Logo and Header */}
          <div className="text-center mb-4 sm:mb-5">
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia™" 
              className="h-12 sm:h-14 w-auto mx-auto rounded-lg shadow-2xl mb-3"
            />
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1.5">
              Welcome to NewAudia<sup className="font-bold text-[0.6em]">TM</sup>
            </h1>
            <p className="text-sm sm:text-base text-purple-200 font-medium">
              The Birthplace of the NFD-SmartBook<sup className="text-[0.6em]">™</sup>
            </p>
          </div>

          {/* Auth Modal Card */}
          <div className="bg-white rounded-xl shadow-2xl p-5 sm:p-6 max-h-[calc(100vh-12rem)] overflow-y-auto">

          {verificationPending ? (
            <div data-testid="verification-pending-screen" className="text-center py-4">
              <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                <Mail className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                Verify Your Email
              </h2>
              <p className="text-sm text-gray-700 mb-3">
                We've sent a verification link to <span className="font-semibold">{verificationPending.email}</span>.
              </p>
              <p className="text-xs text-gray-600 mb-4">
                Please check your inbox and click the link to activate your account and receive your Reader Credits. The link expires in 24 hours.
              </p>
              <Button
                data-testid="resend-verification-btn"
                type="button"
                variant="outline"
                disabled={resendLoading}
                onClick={() => handleResendVerification(verificationPending.email)}
                className="text-sm"
              >
                {resendLoading ? 'Sending…' : 'Resend verification email'}
              </Button>
              {resendMessage && (
                <p className="text-xs text-gray-600 mt-3" data-testid="resend-verification-message">
                  {resendMessage}
                </p>
              )}
              <div className="mt-5 text-xs text-gray-500">
                Already verified?{' '}
                <button
                  type="button"
                  className="text-purple-700 underline"
                  onClick={() => {
                    setVerificationPending(null);
                    setIsLogin(true);
                  }}
                  data-testid="go-to-login-link"
                >
                  Sign in
                </button>
              </div>
            </div>
          ) : (
          <>

          {/* Title */}
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 text-center">
            {isLogin ? 'Sign In' : 'Create Your Account'}
          </h2>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {!isLogin && (
              <>
                {/* Personal Information */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                      <Input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        className={`pl-8 sm:pl-10 text-sm h-9 sm:h-10 ${errors.first_name ? 'border-red-500' : ''}`}
                        placeholder="John"
                      />
                    </div>
                    {errors.first_name && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.first_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                      <Input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        className={`pl-8 sm:pl-10 text-sm h-9 sm:h-10 ${errors.last_name ? 'border-red-500' : ''}`}
                        placeholder="Doe"
                      />
                    </div>
                    {errors.last_name && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.last_name}</p>
                    )}
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Phone *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`pl-8 sm:pl-10 text-sm h-9 sm:h-10 ${errors.phone ? 'border-red-500' : ''}`}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  {errors.phone && (
                    <p className="text-red-500 text-xs mt-0.5">{errors.phone}</p>
                  )}
                </div>
              </>
            )}

            {/* Email */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`pl-8 sm:pl-10 text-sm h-9 sm:h-10 ${errors.email ? 'border-red-500' : ''}`}
                  placeholder="you@example.com"
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-0.5">{errors.email}</p>
              )}
              {emailWarning && !isLogin && (
                <div
                  data-testid="registration-warning-notice"
                  className="mt-2 px-3 py-2 rounded border border-yellow-300 bg-yellow-50 text-yellow-900 text-xs"
                >
                  {emailWarning}
                </div>
              )}
            </div>

            {!isLogin && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Alt Email (Recovery) *
                </label>
                <div className="relative">
                  <Mail className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                  <Input
                    type="email"
                    name="alternate_email"
                    value={formData.alternate_email}
                    onChange={handleInputChange}
                    className={`pl-8 sm:pl-10 text-sm h-9 sm:h-10 ${errors.alternate_email ? 'border-red-500' : ''}`}
                    placeholder="recovery@example.com"
                  />
                </div>
                {errors.alternate_email && (
                  <p className="text-red-500 text-xs mt-0.5">{errors.alternate_email}</p>
                )}
              </div>
            )}

            {/* Password */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                  Password *
                </label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => {
                      setForgotEmail(formData.email);
                      setShowForgotPassword(true);
                      setForgotSuccess(false);
                    }}
                    className="text-xs text-purple-600 hover:text-purple-700 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                <Input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`pl-8 sm:pl-10 pr-10 text-sm h-9 sm:h-10 ${errors.password ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  data-testid="toggle-password-visibility"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-0.5">{errors.password}</p>
              )}
            </div>

            {!isLogin && (
              <>
                {/* Location */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      City *
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                      <Input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className={`pl-8 sm:pl-10 text-sm h-9 sm:h-10 ${errors.city ? 'border-red-500' : ''}`}
                        placeholder="New York"
                      />
                    </div>
                    {errors.city && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.city}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      State *
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                      <Input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className={`pl-8 sm:pl-10 text-sm h-9 sm:h-10 ${errors.state ? 'border-red-500' : ''}`}
                        placeholder="NY"
                      />
                    </div>
                    {errors.state && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.state}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Country *
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                    <Input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className={`pl-8 sm:pl-10 text-sm h-9 sm:h-10 ${errors.country ? 'border-red-500' : ''}`}
                      placeholder="United States"
                    />
                  </div>
                  {errors.country && (
                    <p className="text-red-500 text-xs mt-0.5">{errors.country}</p>
                  )}
                </div>

                {/* Promo Code (Optional) */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Promo Code (optional)
                  </label>
                  <div className="relative">
                    <Tag className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                    <Input
                      type="text"
                      name="promo_code"
                      value={formData.promo_code}
                      onChange={handleInputChange}
                      className="pl-8 sm:pl-10 text-sm h-9 sm:h-10 uppercase"
                      placeholder="Enter promo code"
                      data-testid="promo-code-input"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Have a promo code? Enter it for bonus credits!</p>
                </div>
              </>
            )}

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white h-10 sm:h-11 text-sm font-semibold"
              disabled={loading}
            >
              {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          {/* Google Button - Below Sign In */}
          {isLogin && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3 bg-white text-gray-500">Or</span>
                </div>
              </div>

              <Button
                onClick={handleGoogleAuth}
                variant="outline"
                className="w-full border-2 h-10 sm:h-11 text-sm"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>
            </>
          )}

          {/* DISABLED_FOR_DEPLOYMENT: Create New Account Button (for login view) */}
          {/* Registration enabled for Reader Funnel */}
          {isLogin && (
            <div className="mt-4 sm:mt-5 pt-4 sm:pt-5 border-t border-gray-200">
              <p className="text-xs sm:text-sm text-gray-600 text-center mb-2 sm:mb-3">
                Don't have an account yet?
              </p>
              <Button
                onClick={() => setIsLogin(false)}
                variant="outline"
                className="w-full border-2 border-purple-600 text-purple-600 hover:bg-purple-50 h-10 text-sm"
              >
                <UserPlus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
                Create New Account
              </Button>
            </div>
          )}

          {/* Back to Login (for register view) */}
          {!isLogin && (
            <div className="mt-4 sm:mt-5 pt-4 sm:pt-5 border-t border-gray-200">
              <p className="text-xs sm:text-sm text-gray-600 text-center mb-2 sm:mb-3">
                Already have an account?
              </p>
              <Button
                onClick={() => setIsLogin(true)}
                variant="outline"
                className="w-full border-2 border-purple-600 text-purple-600 hover:bg-purple-50 h-10 text-sm"
              >
                <LogIn className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
                Sign In
              </Button>
            </div>
          )}
          </>
          )}
          </div>

          {/* Cancel Button */}
          <div className="text-center mt-5 sm:mt-6 pt-4 border-t border-gray-200">
            <Button
              onClick={() => navigate('/')}
              data-testid="auth-page-cancel-btn"
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-10 text-sm"
            >
              Cancel
            </Button>
          </div>

          {/* Footer */}
          <div className="text-center mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              NewAudia<sup className="font-bold text-[0.5em]">TM</sup> Systems, LLC
            </p>
          </div>
        </div>
      </div>

      {/* Invalid Login Modal */}
      <Dialog open={showInvalidLoginModal} onOpenChange={setShowInvalidLoginModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>We couldn't log you in</DialogTitle>
            <DialogDescription>
              Your email or password didn't match our records. Please try again or create a new account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handleTryAgain}
              className="flex-1"
            >
              Try Again
            </Button>
            {/* DISABLED_FOR_DEPLOYMENT: Create New Account in error dialog */}
            <Button
              disabled={true}
              onClick={() => {}} // DISABLED_FOR_DEPLOYMENT: was handleCreateNewAccount
              className="flex-1 bg-gray-400 cursor-not-allowed"
              title="Registration is currently disabled"
            >
              Create New Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Welcome Back Modal - shown after successful reader login */}
      <WelcomeBackModal
        isOpen={showWelcomeBackModal}
        onClose={() => setShowWelcomeBackModal(false)}
        user={loggedInUser}
      />

      {/* Role Selection Modal - shown for multi-role users */}
      <RoleSelectionModal
        isOpen={showRoleModal}
        userDisplayName={roleModalUser?.first_name || roleModalUser?.name?.split(' ')[0]}
        identityRoles={getIdentityRoles(roleModalUser?.roles)}
        onSelectRole={onRoleSelected}
      />

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Your Password</DialogTitle>
            <DialogDescription>
              {forgotSuccess 
                ? "Check your email for a password reset link."
                : "Enter your email address and we'll send you a link to reset your password."
              }
            </DialogDescription>
          </DialogHeader>
          
          {!forgotSuccess ? (
            <form onSubmit={async (e) => {
              e.preventDefault();
              setForgotLoading(true);
              try {
                await api.post('/auth/forgot-password', { email: forgotEmail });
                setForgotSuccess(true);
              } catch (err) {
                // Still show success for security (don't reveal if email exists)
                setForgotSuccess(true);
              } finally {
                setForgotLoading(false);
              }
            }}>
              <div className="space-y-4 py-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="pl-10"
                      placeholder="your@email.com"
                      required
                      data-testid="forgot-password-email"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={forgotLoading || !forgotEmail}
                  className="bg-gradient-to-r from-purple-600 to-cyan-600"
                  data-testid="forgot-password-submit"
                >
                  {forgotLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="py-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-800">
                  If an account exists for <strong>{forgotEmail}</strong>, you'll receive a password reset email shortly.
                </p>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Don't see it? Check your spam folder. The link expires in 1 hour.
              </p>
              <DialogFooter>
                <Button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotSuccess(false);
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-cyan-600"
                >
                  Back to Sign In
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AuthPage;